import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Lightbulb,
  CheckCircle2,
  XCircle,
  Clock,
  PlayCircle,
  MoreVertical,
  Trash2,
  FileText,
  ChevronDown,
  ChevronUp,
  Filter,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";
import { useHypotheses, Hypothesis, HypothesisStatus } from "@/hooks/useHypotheses";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";

interface HypothesesTrackerProps {
  workspaceId: string | undefined;
  embedded?: boolean;
}

const STATUS_CONFIG: Record<HypothesisStatus, { label: string; icon: typeof Clock; color: string; bgColor: string }> = {
  pending: {
    label: "Offen",
    icon: Clock,
    color: "text-muted-foreground",
    bgColor: "bg-muted/30",
  },
  in_progress: {
    label: "In Prüfung",
    icon: PlayCircle,
    color: "text-blue-600 dark:text-blue-400",
    bgColor: "bg-blue-500/10",
  },
  validated: {
    label: "Validiert",
    icon: CheckCircle2,
    color: "text-green-600 dark:text-green-400",
    bgColor: "bg-green-500/10",
  },
  invalidated: {
    label: "Widerlegt",
    icon: XCircle,
    color: "text-red-600 dark:text-red-400",
    bgColor: "bg-red-500/10",
  },
};

export function HypothesesTracker({ workspaceId, embedded = false }: HypothesesTrackerProps) {
  const navigate = useNavigate();
  const { hypotheses, isLoading, updateHypothesis, deleteHypothesis, isUpdating } = useHypotheses(workspaceId);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [evidence, setEvidence] = useState("");
  const [statusFilter, setStatusFilter] = useState<HypothesisStatus | "all">("all");

  const filteredHypotheses = statusFilter === "all" 
    ? hypotheses 
    : hypotheses.filter(h => h.status === statusFilter);

  const statusCounts = {
    all: hypotheses.length,
    pending: hypotheses.filter(h => h.status === "pending").length,
    in_progress: hypotheses.filter(h => h.status === "in_progress").length,
    validated: hypotheses.filter(h => h.status === "validated").length,
    invalidated: hypotheses.filter(h => h.status === "invalidated").length,
  };

  const handleStatusChange = (id: string, newStatus: HypothesisStatus) => {
    updateHypothesis({ id, status: newStatus });
  };

  const handleSaveNotes = (id: string) => {
    updateHypothesis({ 
      id, 
      validation_notes: notes || undefined,
      evidence: evidence || undefined,
    });
    setEditingId(null);
    setNotes("");
    setEvidence("");
  };

  const startEditing = (hypothesis: Hypothesis) => {
    setEditingId(hypothesis.id);
    setNotes(hypothesis.validation_notes || "");
    setEvidence(hypothesis.evidence || "");
  };

  if (isLoading) {
    return (
      <div className={cn(
        "relative overflow-hidden transition-all duration-500",
        !embedded && "rounded-2xl border border-border/50 bg-card p-6"
      )}>
        {!embedded && <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />}
        <div className="relative z-10">
          {!embedded && (
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Hypothesen-Tracking</h3>
                <p className="text-sm text-muted-foreground">Wird geladen...</p>
              </div>
            </div>
          )}
          <div className={cn("space-y-3", embedded && "p-6")}>
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-muted/30 rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (hypotheses.length === 0) {
    return (
      <div className={cn(
        "relative overflow-hidden transition-all duration-500",
        !embedded && "rounded-2xl border border-border/50 bg-card p-6 hover:border-primary/20"
      )}>
        {!embedded && <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />}
        <div className={cn("relative z-10", embedded && "p-6")}>
          {!embedded && (
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Hypothesen-Tracking</h3>
                <p className="text-sm text-muted-foreground">Keine Hypothesen vorhanden</p>
              </div>
            </div>
          )}
          <p className={cn(
            "text-sm text-muted-foreground text-center py-6",
            !embedded && "border-t border-border/50"
          )}>
            Hypothesen werden automatisch aus abgeschlossenen Reports importiert.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "relative overflow-hidden transition-all duration-500",
      !embedded && "rounded-2xl border border-border/50 bg-card hover:border-primary/20"
    )}>
      {/* Background decorative elements */}
      {!embedded && <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" />}
      
      {/* Highlight badge */}
      {!embedded && (
        <div className="absolute top-4 right-4 z-20">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
            <Sparkles className="w-3 h-3" />
            {hypotheses.length} Hypothesen
          </span>
        </div>
      )}

      <div className={cn("relative z-10", !embedded && "p-6")}>
        {/* Header */}
        {!embedded && (
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Hypothesen-Tracking</h3>
              <p className="text-sm text-muted-foreground">
                Kritische Annahmen validieren
              </p>
            </div>
          </div>
        )}

        {/* Filter & Actions Row */}
        <div className={cn(
          "flex items-center justify-between gap-2 mb-4 pb-4 border-b border-border/50",
          embedded && "px-6 pt-4"
        )}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 text-xs hover:bg-primary/5">
                <Filter className="h-3.5 w-3.5 mr-1.5" />
                {statusFilter === "all" ? "Alle" : STATUS_CONFIG[statusFilter].label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => setStatusFilter("all")}>
                Alle ({statusCounts.all})
              </DropdownMenuItem>
              {(Object.entries(STATUS_CONFIG) as [HypothesisStatus, typeof STATUS_CONFIG["pending"]][]).map(([status, config]) => (
                <DropdownMenuItem key={status} onClick={() => setStatusFilter(status)}>
                  <config.icon className={cn("h-4 w-4 mr-2", config.color)} />
                  {config.label} ({statusCounts[status]})
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/hypotheses')}
            className="h-8 text-xs hover:bg-primary/5 hover:text-primary"
          >
            Alle anzeigen
            <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>

        {/* Hypotheses List */}
        <div className={cn("space-y-3", embedded && "px-6 pb-6")}>
          {filteredHypotheses.slice(0, 3).map((hypothesis) => {
            const statusConfig = STATUS_CONFIG[hypothesis.status as HypothesisStatus];
            const StatusIcon = statusConfig.icon;
            const isExpanded = expandedId === hypothesis.id;
            const isEditing = editingId === hypothesis.id;

            return (
              <div
                key={hypothesis.id}
                className={cn(
                  "group relative border border-border/50 rounded-xl p-4 transition-all duration-300",
                  "hover:border-primary/20 hover:bg-gradient-to-br hover:from-primary/5 hover:to-transparent",
                  "overflow-hidden",
                  statusConfig.bgColor
                )}
              >
                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 h-[2px] w-0 group-hover:w-full bg-gradient-to-r from-primary to-primary/30 transition-all duration-500" />
                
                {/* Main Row */}
                <div className="flex items-start gap-3">
                  <StatusIcon className={cn("h-4 w-4 shrink-0 mt-0.5", statusConfig.color)} />
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground leading-relaxed">{hypothesis.hypothesis}</p>
                    
                    {/* Session Reference - Collapsible */}
                    {hypothesis.session && (hypothesis.session.summary || hypothesis.session.decision_type) && (
                      <Collapsible>
                        <CollapsibleTrigger asChild>
                          <button className="text-xs text-muted-foreground mt-2 flex items-center gap-1 hover:text-primary transition-colors group/ctx">
                            <FileText className="h-3 w-3" />
                            <span>Kontext</span>
                            <ChevronDown className="h-3 w-3 group-data-[state=open]/ctx:rotate-180 transition-transform" />
                          </button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <p className="text-xs text-muted-foreground mt-2 pl-3 border-l-2 border-primary/20">
                            {hypothesis.session.summary || hypothesis.session.decision_type}
                          </p>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                    
                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="mt-3 space-y-3">
                        {isEditing ? (
                          <>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Notizen</label>
                              <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Erkenntnisse, Beobachtungen..."
                                className="mt-1 text-sm"
                                rows={2}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground">Evidenz</label>
                              <Input
                                value={evidence}
                                onChange={(e) => setEvidence(e.target.value)}
                                placeholder="Link zu Daten, Dokumenten..."
                                className="mt-1 text-sm"
                              />
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => handleSaveNotes(hypothesis.id)}
                                disabled={isUpdating}
                              >
                                Speichern
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => setEditingId(null)}
                              >
                                Abbrechen
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            {hypothesis.validation_notes && (
                              <div className="bg-background/50 rounded-lg p-2">
                                <p className="text-xs font-medium text-muted-foreground mb-1">Notizen</p>
                                <p className="text-sm">{hypothesis.validation_notes}</p>
                              </div>
                            )}
                            {hypothesis.evidence && (
                              <div className="bg-background/50 rounded-lg p-2">
                                <p className="text-xs font-medium text-muted-foreground mb-1">Evidenz</p>
                                <p className="text-sm">{hypothesis.evidence}</p>
                              </div>
                            )}
                            {hypothesis.validated_at && (
                              <p className="text-xs text-muted-foreground">
                                Aktualisiert {formatDistanceToNow(new Date(hypothesis.validated_at), { 
                                  addSuffix: true, 
                                  locale: de 
                                })}
                              </p>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => startEditing(hypothesis)}
                              className="border-primary/20 hover:bg-primary/5"
                            >
                              Notizen bearbeiten
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge 
                      variant="outline" 
                      className={cn("text-[10px] px-2 py-0.5", statusConfig.color, "border-current/20")}
                    >
                      {statusConfig.label}
                    </Badge>
                    
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 hover:bg-primary/10"
                      onClick={() => setExpandedId(isExpanded ? null : hypothesis.id)}
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-primary/10">
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleStatusChange(hypothesis.id, "in_progress")}>
                          <PlayCircle className="h-4 w-4 mr-2 text-blue-600" />
                          Als "In Prüfung" markieren
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleStatusChange(hypothesis.id, "validated")}>
                          <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                          Als validiert markieren
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleStatusChange(hypothesis.id, "invalidated")}>
                          <XCircle className="h-4 w-4 mr-2 text-red-600" />
                          Als widerlegt markieren
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => deleteHypothesis(hypothesis.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Show more indicator */}
        {filteredHypotheses.length > 3 && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/hypotheses')}
            className={cn(
              "w-full mt-4 text-xs text-muted-foreground hover:text-primary hover:bg-primary/5",
              embedded && "mx-6 w-[calc(100%-3rem)] mb-4"
            )}
          >
            +{filteredHypotheses.length - 3} weitere Hypothesen
            <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
